import { useEffect, useState } from 'react';
import {
  Grid,
  Theme,
  FormControl,
  MenuItem,
  Typography,
  Radio,
  FormControlLabel,
  RadioGroup
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Page, Toast, Button as SMButton, PageTitleWithBack, SMBSelect } from '../../../components';
import { Formik, Form, Field } from 'formik';
import { INeighborhood } from '../../../models';
import { Delete } from '@mui/icons-material';
import { DeletePunchListModal } from './DeletePunchListModal';
import { getBuilds, getNeighborhoods } from '../../../fetch';

export const ManagePunchList = () => {
  const classes = useStyles();
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>(null);
  const [lots, setLots] = useState<number[]>(null);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<number | string>('');
  const [selectedLotId, setSelectedLotId] = useState<number | string>('');
  const [isLoadingNeighborhoods, setNeighborhoodsLoading] = useState<boolean>(true);
  const [isLoadingNeighborhoodLots, setNeighborhoodsLotsLoading] = useState<boolean>(false);
  const [isError, showError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const fetchNeighborhoodLots = async () => {
    setNeighborhoodsLotsLoading(true);
    try {
      const res = await getBuilds({
        neighborhoodId: selectedNeighborhoodId,
        isActive: true,
        status: 'New,BuildApproved,contractAccepted,TenDayComplete,Closing'
      });
      if (res) {
        setLots(res.records.map(record => record.lotNumber));
      } else {
        console.error(`No 'records' on lots response.`);
        setErrorMessage('Error loading lots, please try again.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Error loading lots, please try again.');
    }
    setNeighborhoodsLotsLoading(false);
  };

  const fetchNeighborhoods = async () => {
    try {
      const res = await getNeighborhoods();
      if (res) {
        setNeighborhoods(res.records);
      } else {
        console.error(`No 'records' on neighborhoods response.`);
        setErrorMessage('Error loading neighborhoods, please try again.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Error loading neighborhoods, please try again.');
    }
    setNeighborhoodsLoading(false);
  };

  useEffect(() => {
    fetchNeighborhoods();
  }, []);

  useEffect(() => {
    if (selectedNeighborhoodId) {
      fetchNeighborhoodLots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhoodId]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          neighborhoodId: '',
          lotNumber: '',
          walkData: ''
        }}
        onSubmit={async (values, actions) => {}}
      >
        {({ values, setFieldValue, errors, handleBlur }) => {
          return (
            <>
              <Form>
                <Page title="Punch List">
                  <PageTitleWithBack
                    backText="Back to Admin home"
                    backUrl="/admin"
                    title="Punch List"
                  />
                  <Typography className={classes.instructionalText} align="left">
                    Punch List Tools This function will reset a prewalk through or a punch list
                    completion. This feature does not work with purchaser walk throughs.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <SMBSelect
                        handleClear={() => {
                          setFieldValue('neighborhoodId', '');
                          setSelectedNeighborhoodId('');
                          setFieldValue('lotNumber', '');
                        }}
                        onChange={e => {
                          setSelectedNeighborhoodId(e.target.value);
                          setFieldValue('neighborhoodId', e.target.value);
                        }}
                        name={`neighborhoodId`}
                        label={isLoadingNeighborhoods ? 'Loading...' : 'Neighborhood'}
                        variant="outlined"
                        id={`neighborhoodId`}
                        size="small"
                        disabled={isLoadingNeighborhoods}
                        value={values.neighborhoodId}
                        onBlur={handleBlur}
                        error={errors && errors.neighborhoodId ? true : false}
                      >
                        {!isLoadingNeighborhoods && neighborhoods && neighborhoods.length === 0 && (
                          <MenuItem disabled>No Neighborhoods available</MenuItem>
                        )}
                        {!isLoadingNeighborhoods &&
                          neighborhoods &&
                          neighborhoods.map((neighborhood: INeighborhood, index) => {
                            return (
                              <MenuItem key={`${index}`} value={neighborhood.neighborhoodId}>
                                {neighborhood.name} Phase {neighborhood.phase}
                              </MenuItem>
                            );
                          })}
                      </SMBSelect>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SMBSelect
                        variant="outlined"
                        onChange={e => {
                          setFieldValue('lotNumber', e.target.value);
                          setSelectedLotId(e.target.value);
                        }}
                        handleClear={() => {
                          setFieldValue('lotNumber', '');
                          setSelectedLotId('');
                        }}
                        name={`lotNumber`}
                        label={isLoadingNeighborhoodLots ? 'Loading...' : 'Lot'}
                        id={`lotNumber`}
                        size="small"
                        disabled={isLoadingNeighborhoodLots || !selectedNeighborhoodId}
                        value={values.lotNumber}
                        onBlur={handleBlur}
                        error={errors && errors.lotNumber ? true : false}
                      >
                        {!isLoadingNeighborhoodLots && lots && lots.length === 0 && (
                          <MenuItem disabled>No Lots available</MenuItem>
                        )}
                        {!isLoadingNeighborhoodLots &&
                          lots &&
                          lots.map((lot: number, index) => {
                            return (
                              <MenuItem key={`${index}`} value={lot}>
                                {lot}
                              </MenuItem>
                            );
                          })}
                      </SMBSelect>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field name={`walkData`}>
                        {({ field }) => {
                          return (
                            <FormControl component="fieldset">
                              <RadioGroup {...field} aria-label="walkData" name="walkData">
                                <FormControlLabel
                                  value="prewalk"
                                  control={<Radio />}
                                  label="Completely remove all prewalk data."
                                />
                                <FormControlLabel
                                  value="puchlist"
                                  control={<Radio />}
                                  label="Completely remove all puchlist data."
                                />
                              </RadioGroup>
                            </FormControl>
                          );
                        }}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <SMButton
                        id="edit-roles"
                        color="error"
                        startIcon={<Delete />}
                        disabled={!selectedNeighborhoodId || !selectedLotId || !values.walkData}
                        onClick={() => {
                          setDeleteModalOpen(true);
                        }}
                        variant="contained"
                      >
                        Delete
                      </SMButton>
                    </Grid>
                  </Grid>
                </Page>
              </Form>
              {neighborhoods && (
                <DeletePunchListModal
                  open={deleteModalOpen}
                  onClose={() => {
                    setDeleteModalOpen(false);
                  }}
                  onResetForm={() => {
                    setSelectedNeighborhoodId('');
                    setFieldValue('neighborhoodId', '');
                    setSelectedLotId('');
                    setFieldValue('lotNumber', '');
                    setFieldValue('walkData', '');
                    fetchNeighborhoods();
                  }}
                  neighborhood={
                    neighborhoods.filter(hood => hood.neighborhoodId === selectedNeighborhoodId)[0]
                  }
                  lotNumber={Number(selectedLotId)}
                  punchOrPre={values.walkData}
                />
              )}
            </>
          );
        }}
      </Formik>

      <Toast
        id="error"
        autoHideDuration={5000}
        message={errorMessage}
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  instructionalText: {
    padding: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  }
}));
